import { useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import type { ScoreTimeline } from '@/types';
import type { ScrollAnchor } from '@/utils/notationCursor';
import {
  scrollLeftToTime,
  timeToScrollLeft,
  type ScrubMappingContext,
} from '@/utils/notationScrub';
import { usePlaybackStore } from '@/stores';
import { clampSkipTarget } from '@/stores/playbackStore';
import type { SkipContext } from '@/stores/playbackStore';

// Client-side only registration (Vite SPA; guard is defense-in-depth for tooling).
if (typeof window !== 'undefined') {
  gsap.registerPlugin(useGSAP, Draggable, InertiaPlugin);
}

/** Movement (px) below which a press+release is a click, not a drag (no seek). */
export const SCRUB_MIN_MOVEMENT_PX = 3;

export const SCRUB_EDGE_RESISTANCE = 0.85;
export const SCRUB_THROW_RESISTANCE = 3000;
export const SCRUB_MAX_THROW_DURATION = 0.8;

export interface UseNotationScrubOptions {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  measureXMapRef: MutableRefObject<Map<number, number>>;
  beatAnchorsRef?: MutableRefObject<ScrollAnchor[]>;
  timeline: ScoreTimeline | null | undefined;
  /**
   * OTO-26 T4: resolves loop/wait bounds for the settle step (same SkipContext
   * the transport skip buttons use). Declared in T3, honored from T4 on.
   */
  resolveSkipContext?: () => SkipContext | null;
}

/**
 * OTO-26: drag the notation CONTENT to scrub playback. Content-follows-finger:
 * finger left => scrollLeft grows => later notation under the fixed cursor =>
 * time FORWARD; finger right => time BACKWARD. GSAP Draggable drives a proxy
 * element (type 'x'); InertiaPlugin provides glide-to-stop. Live preview seeks
 * via playbackStore.seek; pause-on-drag-start, resume-on-release.
 */
export function useNotationScrub({
  scrollContainerRef,
  contentRef,
  measureXMapRef,
  beatAnchorsRef,
  timeline,
  resolveSkipContext,
}: UseNotationScrubOptions): { isDragging: boolean } {
  const [isDragging, setIsDragging] = useState(false);
  const gestureRef = useRef({
    active: false,
    wasPlaying: false,
    startTime: 0,
    startScrollLeft: 0,
    maxScrollLeft: 0,
    anchorScroll: 0, // FX2: gesture-domain scroll at drag-start time
    virtualScroll: 0, // FX2: virtual scroll position (updated by applyScroll)
  });
  const resolveRef = useRef(resolveSkipContext);
  resolveRef.current = resolveSkipContext;

  useGSAP(
    () => {
      const container = scrollContainerRef.current;
      if (!container || !timeline) return;

      const mappingCtx = (): ScrubMappingContext => ({
        measures: timeline.measures,
        totalDuration: timeline.totalDuration,
        measureXMap: measureXMapRef.current,
        beatAnchors: beatAnchorsRef?.current,
        contentScrollWidth: contentRef.current?.scrollWidth ?? container.scrollWidth,
        containerWidth: container.clientWidth,
      });

      const applyScroll = (x: number) => {
        const g = gestureRef.current;
        // Content-follows-finger: finger left (x < 0) => scrollLeft grows => time FORWARD.
        // Physical scrollLeft stays clamped to [0, maxScrollLeft] (DOM constraint).
        const physicalScroll = Math.min(g.maxScrollLeft, Math.max(0, g.startScrollLeft - x));
        container.scrollLeft = physicalScroll;
        // FX2: separate virtual scroll for time preview (anchored at drag-start,
        // clamped by the gesture bounds, not the physical bounds).
        g.virtualScroll = Math.min(g.maxScrollLeft, Math.max(0, g.anchorScroll - x));
      };

      const previewTarget = (): number => {
        const g = gestureRef.current;
        // FX2: invert from virtual scroll (gesture-domain), not physical scrollLeft.
        const raw = scrollLeftToTime(g.virtualScroll, mappingCtx(), g.startTime);
        const ctx = resolveRef.current?.() ?? null;
        if (ctx) return clampSkipTarget(raw, g.startTime, ctx);
        return Math.min(timeline.totalDuration, Math.max(0, raw));
      };

      const previewSeek = () => {
        if (!gestureRef.current.active) return;
        usePlaybackStore.getState().seek(previewTarget());
      };

      const settleGesture = () => {
        const g = gestureRef.current;
        if (!g.active) return;
        g.active = false;
        const store = usePlaybackStore.getState();
        store.seek(previewTarget());
        const ctx = resolveRef.current?.() ?? null;
        // skipBy(0, ctx): re-clamps (no-op by construction) and performs the
        // EXACT wait-mode chord re-arm / pause-when-nothing-left of a button skip.
        if (ctx) usePlaybackStore.getState().skipBy(0, ctx);
        if (g.wasPlaying) usePlaybackStore.getState().play();
        setIsDragging(false);
      };

      // The proxy MUST be attached to the live DOM: GSAP's internal x/y
      // tracking (gsap.set / matrix inversion) on a disconnected node
      // silently produces NaN. Kept invisible and non-interactive.
      const proxy = document.createElement('div');
      proxy.style.position = 'fixed';
      proxy.style.opacity = '0';
      proxy.style.pointerEvents = 'none';
      document.body.appendChild(proxy);
      const drag = Draggable.create(proxy, {
        type: 'x',
        trigger: container,
        inertia: true,
        minimumMovement: SCRUB_MIN_MOVEMENT_PX,
        edgeResistance: SCRUB_EDGE_RESISTANCE,
        throwResistance: SCRUB_THROW_RESISTANCE,
        maxDuration: SCRUB_MAX_THROW_DURATION,
        onPress(this: Draggable, event: MouseEvent | TouchEvent | PointerEvent) {
          // Left-button-only for mouse (OTO-28 gesture-hygiene precedent).
          const button = (event as MouseEvent).button;
          if (typeof button === 'number' && button !== 0) {
            this.endDrag(event);
            return;
          }
          gsap.set(proxy, { x: 0 });
          this.update(true);
          const g = gestureRef.current;
          g.startScrollLeft = container.scrollLeft;
          g.maxScrollLeft = Math.max(
            0,
            (contentRef.current?.scrollWidth ?? container.scrollWidth) - container.clientWidth
          );
        },
        onDragStart(this: Draggable) {
          const store = usePlaybackStore.getState();
          const g = gestureRef.current;
          g.wasPlaying = store.state === 'playing';
          if (g.wasPlaying) store.pause(); // derives exact currentTime from anchor
          g.startTime = usePlaybackStore.getState().position.currentTime;
          // FX2: compute the gesture-domain anchor scroll from the current time.
          // This is the virtual reference point; it stays independent of the
          // physical scrollLeft the cursor wrote (which uses a different offset).
          g.anchorScroll = timeToScrollLeft(g.startTime, mappingCtx());
          g.virtualScroll = g.anchorScroll; // initialize virtual scroll at anchor
          // Fix2: bounds must use VIRTUAL-anchor-relative domain, not physical-start-relative,
          // to avoid shifted edge targets when anchorScroll !== startScrollLeft.
          this.applyBounds({
            minX: g.anchorScroll - g.maxScrollLeft,
            maxX: g.anchorScroll,
            minY: 0,
            maxY: 0,
          });
          g.active = true;
          setIsDragging(true);
        },
        onDrag(this: Draggable) {
          applyScroll(this.x);
          previewSeek();
        },
        onThrowUpdate(this: Draggable) {
          applyScroll(this.x);
          previewSeek();
        },
        onDragEnd(this: Draggable) {
          if (!this.isThrowing) settleGesture();
        },
        onThrowComplete(this: Draggable) {
          settleGesture();
        },
      })[0];

      // End-gesture-on-window-blur (OTO-28 precedent, fix commit 683f9cc).
      const onWindowBlur = () => {
        if (!gestureRef.current.active) return;
        if (drag.isPressed) drag.endDrag(drag.pointerEvent);
        if (drag.tween) drag.tween.kill();
        settleGesture(); // idempotent via g.active guard
      };
      window.addEventListener('blur', onWindowBlur);

      return () => {
        window.removeEventListener('blur', onWindowBlur);
        // Abort semantics for unmount / timeline-dependency change MID-DRAG
        // (useGSAP runs this cleanup in both cases): the mapping context may
        // already be invalid (new score), so do NOT settle/seek. Cancel the
        // gesture, kill any in-flight throw, and honor the resume-on-release
        // intent for the paused-for-drag transport.
        const g = gestureRef.current;
        if (g.active) {
          g.active = false;
          if (drag.tween) drag.tween.kill();
          if (g.wasPlaying) usePlaybackStore.getState().play();
          setIsDragging(false);
        }
        drag.kill();
        proxy.remove();
      };
    },
    // FX1: default revertOnUpdate:false would RETAIN the previous Draggable+
    // proxy across dependency re-runs (StrictMode double-invoke, timeline ref
    // change), stacking live instances that share gestureRef and clobber
    // wasPlaying -> resume-on-release breaks. revert-per-update runs our
    // cleanup (kill + proxy.remove + mid-drag abort) before each re-setup.
    { dependencies: [timeline], scope: scrollContainerRef, revertOnUpdate: true }
  );

  return { isDragging };
}
